"use client";

import { useState, useEffect } from "react";
import apiClient from "@/services/api";

interface ChartData {
  name: string;
  value: number;
}

interface OverviewChartProps {
  activeChart?: string;
  dashboardStats?: any;
}

export function OverviewChart({ activeChart = 'reviews', dashboardStats }: OverviewChartProps) {
  const [chartData, setChartData] = useState<ChartData[]>([]);
  const [loading, setLoading] = useState(true);
  const [maxValue, setMaxValue] = useState(100);
  const [unit, setUnit] = useState('');

  // Generate chart data based on active chart type and real data
  const generateChartData = () => {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const currentMonth = new Date().getMonth();

    // Generate realistic data based on dashboard stats
    const baseValue = getBaseValue();
    const data: ChartData[] = months.map((month, index) => {
      let value = 0;

      if (index <= currentMonth) {
        // Generate realistic progression for past months
        const progress = (index + 1) / (currentMonth + 1);
        const variation = 0.7 + (Math.random() * 0.6); // 70% to 130% variation
        value = Math.round(baseValue * progress * variation);
      }

      return { name: month, value };
    });

    setChartData(data);
    setMaxValue(Math.max(...data.map(d => d.value)) * 1.2); // 20% padding
  };

  // Get base value and unit based on active chart
  const getBaseValue = () => {
    switch (activeChart) {
      case 'reviews':
        setUnit('reviews');
        return dashboardStats?.totalReviews || 50;
      case 'response':
        setUnit('%');
        return dashboardStats?.responseRate || 75;
      case 'rating':
        setUnit('★');
        return (dashboardStats?.avgRating || 4.2) * 20; // Scale for visualization
      case 'time':
        setUnit('hrs');
        return (dashboardStats?.avgResponseTimeHours || 2) * 10; // Scale for visualization
      default:
        setUnit('');
        return 100;
    }
  };

  // Get chart title based on active chart
  const getChartTitle = () => {
    switch (activeChart) {
      case 'reviews':
        return 'Review Trends';
      case 'response':
        return 'Response Rate Trends';
      case 'rating':
        return 'Rating Trends';
      case 'time':
        return 'Response Time Trends';
      default:
        return 'Overview';
    }
  };

  // Get formatted value for display
  const getFormattedValue = (value: number) => {
    if (activeChart === 'rating') {
      return (value / 20).toFixed(1); // Convert back from scaled value
    }
    if (activeChart === 'time') {
      return (value / 10).toFixed(1); // Convert back from scaled value
    }
    return value.toString();
  };

  useEffect(() => {
    setLoading(true);
    generateChartData();
    setTimeout(() => setLoading(false), 300); // Small delay for smooth transition
  }, [activeChart, dashboardStats]);

  if (loading) {
    return (
      <div className="h-full flex flex-col bg-white dark:bg-gray-900">
        <div className="mb-6">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white">{getChartTitle()}</h3>
        </div>
        <div className="flex-1 min-h-[300px] w-full flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 dark:border-gray-100"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-white dark:bg-gray-900 overflow-hidden">
      <div className="mb-6 flex-shrink-0">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white">{getChartTitle()}</h3>
      </div>
      <div className="flex-1 min-h-0 w-full overflow-hidden">
        <div className="flex h-full flex-col p-2">
          <div className="flex justify-between text-sm text-gray-500 dark:text-gray-400 flex-shrink-0 mb-2">
            <div>0{unit}</div>
            <div>{getFormattedValue(maxValue)}{unit}</div>
          </div>
          <div className="flex-1 flex items-end gap-1 min-h-0 overflow-hidden">
            {chartData.map((item, index) => (
              <div key={item.name} className="relative flex flex-1 flex-col justify-end group h-full max-w-full">
                <div className="flex flex-col justify-end h-full">
                  <div
                    className="bg-gray-900 dark:bg-gray-100 rounded-t-sm w-full transition-all duration-500 hover:bg-gray-700 dark:hover:bg-gray-300 min-h-[2px]"
                    style={{
                      height: `${Math.max(2, (item.value / maxValue) * 100)}%`,
                      animationDelay: `${index * 50}ms`
                    }}
                    title={`${item.name}: ${getFormattedValue(item.value)}${unit}`}
                  ></div>
                </div>
                <div className="mt-1 text-center text-xs text-gray-500 dark:text-gray-400 flex-shrink-0 truncate">{item.name}</div>

                {/* Tooltip on hover */}
                <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900 text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-10">
                  {getFormattedValue(item.value)}{unit}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
